import type { FaceLandmarker } from '@mediapipe/tasks-vision';
import workerUrl from './face.worker.ts?worker&url';
import { createFaceLandmarker, packLargestFace } from './landmarkerFactory';
import { DEFAULT_TRACKER_OPTIONS, type Delegate, type FromWorkerMessage, type ToWorkerMessage, type TrackerInitOptions, type TrackerResult } from './protocol';

export type TrackerMode = 'worker' | 'inline';

export interface FaceTrackerConfig extends TrackerInitOptions {
  /** 'auto' picks the worker when the platform supports it. */
  mode: 'auto' | TrackerMode;
  /** Absolute URL of the built worker script (defaults to the bundle's own sibling file). */
  workerUrl?: string;
}

export interface TrackerStatus {
  mode: TrackerMode;
  delegate: Delegate;
  initMs: number;
}

type ResultListener = (r: TrackerResult) => void;
type ErrorListener = (message: string, fatal: boolean) => void;

/** A110Y: Blob URL of the same-origin trampoline of each worker, until it has started. */
const trampolineUrls = new WeakMap<Worker, string>();

/**
 * Main-thread facade over the tracking worker, with an inline fallback that
 * runs the landmarker on the UI thread (older Safari without OffscreenCanvas,
 * or when worker start-up fails).
 *
 * Back-pressure: at most one frame in flight. The caller checks `busy` and
 * simply skips grabbing a frame when the tracker is still working, so the
 * pipeline always processes the freshest frame and latency never accumulates.
 */
export class FaceTracker {
  private readonly config: FaceTrackerConfig;
  private worker: Worker | null = null;
  private inline: FaceLandmarker | null = null;
  private status: TrackerStatus | null = null;
  private inFlight = false;
  /** A110X: when the frame in flight was posted (performance.now()), to spot a worker that stopped answering. */
  private inFlightSince = -1;
  private seq = 0;
  /** A110X: bumped by restart(); an older init still running gives up instead of installing itself. */
  private generation = 0;
  private disposed = false;
  private cancelInit: (() => void) | null = null;
  private lastTimestamp = -1;
  private resultListeners: ResultListener[] = [];
  private errorListeners: ErrorListener[] = [];

  constructor(config: Partial<FaceTrackerConfig> & Pick<FaceTrackerConfig, 'wasmBasePath' | 'modelAssetPath'>) {
    this.config = { mode: 'auto', ...DEFAULT_TRACKER_OPTIONS, ...config };
  }

  static workerSupported(): boolean {
    return typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function';
  }

  async init(): Promise<TrackerStatus> {
    if (this.disposed) throw new Error("Tracker disposed");
    // A110Y: a worker left by a fatal error (« Réessayer ») is closed before a new one starts.
    if (this.worker) {
      FaceTracker.closeWorker(this.worker);
      this.worker = null;
      this.status = null;
      this.inFlight = false;
      this.inFlightSince = -1;
    }
    const generation = this.generation;
    const wantWorker = this.config.mode === 'worker' || (this.config.mode === 'auto' && FaceTracker.workerSupported());
    if (wantWorker) {
      try {
        const status = await this.initWorker();
        if (generation !== this.generation) throw new Error('Tracker restarted');
        this.status = status;
        return this.status;
      } catch (err) {
        if (this.disposed || generation !== this.generation) throw err;
        console.warn('[tracker] worker start-up failed, falling back to inline mode:', err);
        if (this.worker) FaceTracker.closeWorker(this.worker);
        this.worker = null;
      }
    }
    const status = await this.initInline();
    if (generation !== this.generation) throw new Error('Tracker restarted');
    this.status = status;
    return this.status;
  }

  /**
   * Creates the module worker. When the bundle is served from a CDN (Shopify
   * embed), `new Worker(crossOriginUrl)` is forbidden by the browser, so we go
   * through a same-origin Blob trampoline that imports the real script (the
   * CDN must send `Access-Control-Allow-Origin`).
   */
  static createWorker(explicitUrl?: string): Worker {
    const url = explicitUrl ? new URL(explicitUrl, location.href) : new URL(workerUrl, import.meta.url);
    const sameOrigin = url.origin === location.origin || url.protocol === 'blob:';
    if (sameOrigin) return new Worker(url, { type: 'module', name: 'mata-face-tracker' });
    const blob = new Blob([`import ${JSON.stringify(url.href)};`], { type: 'text/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    const worker = new Worker(blobUrl, { type: 'module', name: 'mata-face-tracker' });
    // A110Y: revoked once the worker has started (it was never revoked: one Blob URL per worker).
    trampolineUrls.set(worker, blobUrl);
    return worker;
  }

  /**
   * A110Y: closes a worker properly. Before, `close` was posted and `terminate()` called in the
   * same tick, so the worker's `landmarker.close()` never ran and MediaPipe's GPU textures and
   * wasm graph were left to the garbage collector (late on Safari). Now the worker gets up to
   * 400 ms to acknowledge, then it is terminated either way.
   */
  static closeWorker(worker: Worker): void {
    let done = false;
    const kill = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      worker.terminate();
    };
    worker.onmessage = (ev: MessageEvent<FromWorkerMessage>) => {
      if (ev.data?.type === 'closed') kill();
    };
    worker.onerror = () => kill();
    const timer = setTimeout(kill, 400);
    try {
      const msg: ToWorkerMessage = { type: 'close' };
      worker.postMessage(msg);
    } catch {
      kill();
    }
    FaceTracker.revokeTrampoline(worker);
  }

  private static revokeTrampoline(worker: Worker): void {
    const url = trampolineUrls.get(worker);
    if (!url) return;
    trampolineUrls.delete(worker);
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }

  private initWorker(): Promise<TrackerStatus> {
    return new Promise((resolve, reject) => {
      const worker = FaceTracker.createWorker(this.config.workerUrl);
      this.worker = worker;
      const t0 = performance.now();
      const timeout = setTimeout(() => reject(new Error('worker init timeout (30 s)')), 30_000);
      this.cancelInit = () => { clearTimeout(timeout); reject(new Error("Tracker disposed")); };

      worker.onmessage = (ev: MessageEvent<FromWorkerMessage>) => {
        // A110X: a worker replaced by restart() may still have a message queued.
        if (worker !== this.worker) return;
        const msg = ev.data;
        switch (msg.type) {
          case 'ready':
            clearTimeout(timeout);
            this.cancelInit = null;
            FaceTracker.revokeTrampoline(worker);
            resolve({ mode: 'worker', delegate: msg.delegate, initMs: performance.now() - t0 });
            break;
          case 'result':
            this.inFlight = false;
            this.emitResult(msg);
            break;
          case 'error':
            this.inFlight = false;
            if (msg.fatal && !this.status) {
              clearTimeout(timeout);
              reject(new Error(msg.message));
            } else {
              this.emitError(msg.message, msg.fatal);
            }
            break;
        }
      };
      worker.onerror = (ev) => {
        if (worker !== this.worker) return;
        clearTimeout(timeout);
        const message = ev.message || 'worker error';
        if (!this.status) reject(new Error(message));
        else this.emitError(message, true);
      };

      const { mode: _mode, workerUrl: _workerUrl, ...options } = this.config;
      const init: ToWorkerMessage = { type: 'init', options };
      worker.postMessage(init);
    });
  }

  private async initInline(): Promise<TrackerStatus> {
    const t0 = performance.now();
    const { mode: _mode, workerUrl: _workerUrl, ...options } = this.config;
    const generation = this.generation;
    const created = await createFaceLandmarker(options);
    if (this.disposed) { created.landmarker.close(); throw new Error("Tracker disposed"); }
    if (generation !== this.generation) { created.landmarker.close(); throw new Error('Tracker restarted'); }
    this.inline = created.landmarker;
    return { mode: 'inline', delegate: created.delegate, initMs: performance.now() - t0 };
  }

  get busy(): boolean {
    return this.inFlight;
  }

  /** A110X: how long the frame in flight has been waiting for its result (0 when idle). */
  busyForMs(nowMs: number): number {
    return this.inFlight && this.inFlightSince >= 0 ? Math.max(0, nowMs - this.inFlightSince) : 0;
  }

  /**
   * A110X: replaces the worker (or the inline landmarker) by a fresh one with
   * the same options and listeners. Used when the tracker stops answering or
   * comes back slow after the phone slept (lost GPU context in the worker):
   * the same cure as reloading the page, without the camera or the 3D.
   */
  async restart(): Promise<TrackerStatus> {
    if (this.disposed) throw new Error('Tracker disposed');
    this.generation++;
    this.cancelInit?.();
    this.cancelInit = null;
    if (this.worker) {
      const old = this.worker;
      this.worker = null;
      FaceTracker.closeWorker(old);
    }
    this.inline?.close();
    this.inline = null;
    this.status = null;
    this.inFlight = false;
    this.inFlightSince = -1;
    this.lastTimestamp = -1;
    return this.init();
  }

  getStatus(): TrackerStatus | null {
    return this.status;
  }

  /**
   * Submits a frame. Worker mode takes ownership of the ImageBitmap.
   * Returns false when the tracker is busy (frame skipped) or not ready.
   */
  detect(source: ImageBitmap | HTMLVideoElement, timestampMs: number, captureTs: number): boolean {
    if (this.inFlight || !this.status) {
      if (source instanceof ImageBitmap) source.close();
      return false;
    }
    const seq = ++this.seq;
    const ts = timestampMs <= this.lastTimestamp ? this.lastTimestamp + 1 : timestampMs;
    this.lastTimestamp = ts;

    if (this.worker) {
      if (!(source instanceof ImageBitmap)) throw new Error('worker mode requires an ImageBitmap');
      this.inFlight = true;
      this.inFlightSince = performance.now();
      const msg: ToWorkerMessage = { type: 'frame', bitmap: source, timestampMs: ts, captureTs, seq };
      this.worker.postMessage(msg, [source]);
      return true;
    }

    if (this.inline) {
      const width = source instanceof ImageBitmap ? source.width : source.videoWidth;
      const height = source instanceof ImageBitmap ? source.height : source.videoHeight;
      const t0 = performance.now();
      try {
        const result = this.inline.detectForVideo(source, ts);
        const packed = packLargestFace(result);
        this.emitResult({
          type: 'result',
          seq,
          timestampMs: ts,
          captureTs,
          inferMs: performance.now() - t0,
          landmarks: packed?.landmarks ?? null,
          matrix: packed?.matrix ?? null,
          faceCount: packed?.faceCount ?? 0,
          width,
          height,
        });
      } catch (err) {
        this.emitError(err instanceof Error ? err.message : String(err), false);
      } finally {
        if (source instanceof ImageBitmap) source.close();
      }
      return true;
    }
    return false;
  }

  setNumFaces(numFaces: number): void {
    this.config.numFaces = numFaces;
    if (this.worker) {
      const msg: ToWorkerMessage = { type: 'setNumFaces', numFaces };
      this.worker.postMessage(msg);
    } else if (this.inline) {
      void this.inline.setOptions({ numFaces });
    }
  }

  onResult(listener: ResultListener): () => void {
    this.resultListeners.push(listener);
    return () => {
      this.resultListeners = this.resultListeners.filter((l) => l !== listener);
    };
  }

  onError(listener: ErrorListener): () => void {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== listener);
    };
  }

  private emitResult(r: TrackerResult): void {
    for (const l of this.resultListeners) l(r);
  }

  private emitError(message: string, fatal: boolean): void {
    for (const l of this.errorListeners) l(message, fatal);
  }

  dispose(): void {
    this.disposed = true;
    this.cancelInit?.();
    this.cancelInit = null;
    if (this.worker) {
      FaceTracker.closeWorker(this.worker);
      this.worker = null;
    }
    this.inline?.close();
    this.inline = null;
    this.status = null;
    this.inFlight = false;
    this.inFlightSince = -1;
    this.resultListeners = [];
    this.errorListeners = [];
  }
}
